import type { ButtonHTMLAttributes } from "react";
import type {
  InboxDismissChip,
  InboxReasonChip,
  RecipientAttachment,
  RecipientKnock,
} from "../../lib/api";
import { DECIDED_UNREAD_LINE } from "../../lib/conversations";
import { formatCount, relativeTime } from "../../lib/format";
import { CHIP_FULL } from "../shell/chip";
import {
  BAND_PILL,
  BAND_WORD,
  DECISION_PILL,
  DISMISS_LABEL,
  DISMISS_NOTE_MAX,
  DISMISS_OTHER_NOTE,
  DISMISS_OTHER_PLACEHOLDER,
  DISMISS_OTHER_SEND,
  REASON_CHIPS,
  REASON_LABEL,
  NOT_RECOMMENDED,
  UNRANKED_LABEL,
  claimsLine,
  counted,
  decisionOf,
  dismissChipsFor,
  guardClosedLine,
  nobCallReason,
  notRecommended,
  percentileLabel,
  receiptsLine,
  senderLine,
  senderName,
} from "./labels";
import { openPanel } from "./use-inbox-actions";

/** The interactive state of one card, all of it lifted so the card is a pure
 * render (which is how the reveal states can be asserted with static markup). */
export type CardUi = {
  expanded: boolean;
  composerOpen: boolean;
  replyDraft: string;
  /** The "Not for me" reason chips are showing. */
  dismissOpen: boolean;
  /** Inside the dismissal: "Other" was picked, so the one-line box is showing. */
  dismissOther: boolean;
  dismissNote: string;
  /** The inline "Block this sender at your door?" confirm is showing. */
  blockOpen: boolean;
  /** The "Not helpful" reason chips are showing. */
  feedbackOpen: boolean;
  feedbackDone: "helpful" | "not_helpful" | null;
  /** The 30-second Undo affordance, with the word it reverses. `null` once spent. */
  pending: { verb: string } | null;
  /** The dry one-word confirm — "Sent.", "Saved.", "Blocked." */
  note: string | null;
  busy: boolean;
};

export type CardHandlers = {
  onToggleExpand: () => void;
  onReply: () => void;
  onBook: () => void;
  onSave: () => void;
  onNotInterested: () => void;
  onPickDismissReason: (chip: InboxDismissChip) => void;
  onDismissNote: (value: string) => void;
  onSendDismissNote: () => void;
  onCancelDismiss: () => void;
  onBlock: () => void;
  onConfirmBlock: () => void;
  onCancelBlock: () => void;
  onReplyDraft: (value: string) => void;
  onSendReply: () => void;
  onCancelReply: () => void;
  onHelpful: () => void;
  onNotHelpful: () => void;
  onPickFeedbackReason: (chip: InboxReasonChip) => void;
  onUndo: () => void;
};

type Props = {
  /**
   * The list's knock, or the drill-down's — which also carries the files, so
   * the card can name each one (wave 83). A card without them says only that
   * there are files.
   */
  knock: RecipientKnock & { attachments?: RecipientAttachment[] };
  ui: CardUi;
  handlers: CardHandlers;
};

export const BLOCK_ASK = "Block this sender at your door?";
export const BLOCK_NOTE =
  "This conversation leaves your lists and they cannot knock here again. " +
  "Nothing is deleted, and you can lift it from Blocked users in your door settings.";

/**
 * THE DOOR METAPHOR, ALL THE WAY DOWN (owner, 2026-08-26).
 *
 * "Reply" named the typing, not the consequence — and the consequence is the
 * whole product: answering somebody is what lets them in. The wire verb stays
 * `reply`, because that is what the server records and what the sender's
 * transcript calls it; this is what the owner reads.
 */
export const OPEN_THE_DOOR = "Open the door";
/**
 * The other two verbs, named once. They were literals in the row below until
 * the knock chat grew a button of its own (R-0910-13 rule 3): the same verb
 * offered in two places has to read identically in both, and a second copy of
 * the word is how that stops being true.
 */
export const NOT_FOR_ME = "Not for me";
export const BLOCK_VERB = "Block";
export const THROUGH_HINT =
  "Opening the door is what lets them through. Nothing is deleted either way.";
/**
 * The pill, and the line where the summary goes, until a read is published
 * (§6.2). The verbs are NOT waiting on it — they are offered at every moment
 * (§3) — so the line no longer promises their arrival; what is still coming is
 * the doorman's read.
 */
export const HEARING_PILL = "Hearing in progress";
export const HEARING_LINE =
  "Your Nob is still hearing them out. Its read arrives when it is done — you can decide before then.";

/**
 * And the pill for a hearing the GUARD stopped (§8), which is neither running
 * nor read: "Hearing in progress" over "The doorman stopped this hearing" was
 * the card contradicting itself in two adjacent lines. It is not a band and not
 * a decision — the gate did not move — so it wears the neutral edge the
 * in-flight pill wears.
 */
export const STOPPED_PILL = "Stopped";

/**
 * What the card says when the GUARD stopped the doorman listening (§8) is
 * `guardClosedLine` in `labels.ts` — one sentence bank, read here and by the
 * desk, so the two surfaces cannot say different things about one stop.
 *
 * A SAFETY STOP AND NOT A JUDGEMENT: the sentence says what happened and offers
 * no verdict, and the verbs stay under it, because the gate did not move and
 * the owner may still open the door, decline or block.
 */

/**
 * THE VERBS ARE BUTTONS NOW (owner, 2026-08-26): "they look like plain text".
 *
 * They were borderless muted labels in a row, which is what a caption looks
 * like — an owner deciding about a stranger had no edge to aim at. The
 * treatment is `ui/button`'s secondary, sized down to a pill that fits a row of
 * five: the same border token, the same hover raise, the same focus ring.
 *
 * `.nob-verb` is the skin's hook and carries nothing in the two bright themes.
 * Nob mode draws in ink, so there a pill gains the 2px outline and the hard
 * offset shadow every raised object in that theme wears (styles.css).
 */
const VERB =
  "nob-verb inline-flex h-8 items-center rounded-full border px-3 text-sm font-medium " +
  "transition-colors duration-75 focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-focus/70 focus-visible:ring-offset-2 focus-visible:ring-offset-bg " +
  "disabled:pointer-events-none disabled:opacity-50";

const VERB_SECONDARY = `${VERB} border-border text-text-soft hover:border-border-loud hover:bg-surface`;

/** One primary per card, and it is the one that opens the door. */
const VERB_PRIMARY = `${VERB} border-accent bg-accent text-accent-fg hover:bg-accent-soft`;

const ACTION =
  "text-sm text-muted transition-colors duration-75 hover:text-text " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-50 " +
  "disabled:pointer-events-none rounded";

function ActionButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return <button type="button" {...rest} className={`${ACTION} ${className}`} />;
}

/**
 * A verb, with the state that says it is not this button's turn.
 *
 * `aria-disabled` as well as `disabled`, because the owner's complaint was that
 * a pressed verb looked like nothing had happened: the muted, unreachable
 * siblings are half of what says something DID.
 */
export function VerbButton({
  primary = false,
  locked,
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { primary?: boolean; locked: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={locked}
      aria-disabled={locked || undefined}
      className={`${primary ? VERB_PRIMARY : VERB_SECONDARY} ${className}`}
    />
  );
}

/**
 * The FR-6.3 card. The recipient reads what the sender never can — the score,
 * the percentile, the band, the summary written about them — so the standing is
 * shown as a number, not withheld. Spend leads the receipt line with the tooltip
 * that it did not move the ranking (invariant (a)). Expandable to the whole
 * grilling.
 *
 * A vertical stack, deliberately. The card lives in the shell's ~600px column,
 * and the previous three-column grid (identity 12rem · summary · standing)
 * starved the summary — the one thing the card exists to show — to a few words'
 * width. In 600px there is room for exactly one column, so the hierarchy is
 * top-to-bottom: who (with the standing pill on the same line), what they said,
 * what it matched, what the vetting found.
 *
 * THE TRANSCRIPT IS NO LONGER IN HERE (ruling 2026-08-25 #9). Wave 2 folded the
 * grilling into the card behind a disclosure; the knock is a chat window now, so
 * the transcript IS the screen and the card is its first line — see
 * `inbox/knock-chat.tsx`. The fold and its localStorage marks are gone outright.
 */
export function KnockCard({ knock, ui, handlers }: Props) {
  const { standing: s, sender, receipts } = knock;
  // The recipient is allowed the number: the rank as a percentile — "Top 4% · 96".
  // Below MIN_RANK_SAMPLE the door cannot rank at all, and says so rather than
  // inventing a position (the server sends percentile: null there).
  const rank = percentileLabel(s.percentile);
  /**
   * THE PILL SAYS THE DECISION ONCE THERE IS ONE (owner item 9, 2026-08-26).
   *
   * A rank is what the door made of somebody before the owner had. Once the
   * owner has answered, sent back or saved, that is the fact about this knock,
   * and the same precedence runs in the skin's stamp and the desk's Stage row
   * so the three themes cannot say three different things (`decisionOf`).
   */
  const decision = decisionOf(knock);
  /**
   * A KNOCK WITH NO READ ON IT HAS NOTHING TO PRINT ABOUT ITSELF (§6.2). The
   * server sends no band, no rank, no summary and no money until an assessment
   * exists, and this is the allowlist read on the client so a missing field can
   * never be drawn as a plausible zero. Every standing-bearing thing on this
   * card is behind it.
   *
   * WHAT IT NO LONGER GATES IS THE VERBS. R-0905-5 (b)'s rule that they 404
   * until the knock is scored is superseded (§3): the owner watching a hearing
   * in flight decides on what they have read so far.
   */
  const assessed = knock.assessed;
  const pill =
    decision?.word ??
    (knock.guard_closed
      ? STOPPED_PILL
      : !assessed
        ? HEARING_PILL
        : rank && s.percentile !== null
          ? `${rank} · ${formatCount(s.percentile)}`
          : UNRANKED_LABEL);
  const pillClass = decision
    ? DECISION_PILL[decision.tone]
    : // A stop and an unread hearing wear the same neutral edge: neither is a
      // band, and neither is a decision about anybody.
      knock.guard_closed || !assessed
      ? "border-border-strong text-text-soft"
      : BAND_PILL[s.band];
  /**
   * A THREAD THAT HAS BEEN LET THROUGH IS A CONVERSATION, NOT A DOOR (owner
   * item 23). Save, Not for me, Block and Open the door are all decisions made
   * AT a door; once it is open they are the wrong four things to offer under a
   * chat, so the card keeps the standing and drops the verbs. The stage is read
   * exactly as `lib/conversations.ts` reads it, so the pane and the card cannot
   * disagree about which thread this is.
   */
  const through = knock.gate === "let_in";
  /**
   * Whose turn it is. One panel is open at a time (`use-inbox-actions.ts`), and
   * while it is, every verb but the one that opened it is out of reach.
   */
  const panel = openPanel(ui);
  const locked = (owner: string | null) => ui.busy || (panel !== null && panel !== owner);
  const meta = [
    senderLine(sender.account_age_days, sender.followers, sender.company_url),
    knock.created_at ? relativeTime(knock.created_at) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const vetting = receiptsLine(receipts);
  /**
   * NOB'S CALL (ruling R-0925-1 (2)). A knock Nob did not recommend says so,
   * with the reason in one line, and "Open the door" steps down from the one
   * primary on the card to a verb like the others — quieter, never gone. A
   * recommended knock, and one with no call yet, keep it as it was.
   */
  const unrecommended = notRecommended(knock);
  /**
   * THE FILES, BY NAME (wave 83). A voice note is drawn in the conversation
   * as the note it is; the documents and images are listed here, each with
   * its pages and whether the doorman read it.
   */
  const files = (knock.attachments ?? []).filter((a) => !a.mime_type.startsWith("audio/"));

  return (
    <li
      /* The rail's verbs mutate THIS, at the top of a scroller the rail is not
         in — so the rail scrolls it into view when it acts (`shell/desk.ts`).
         The attribute is the handle it reaches for. */
      data-knock-card
      className="rounded-xl border border-border bg-surface/20 p-4 sm:p-5"
    >
      {/* Who is at the door, with the standing — the two glanceable facts.
          On a phone the pill goes under the name rather than beside it
          (wave 83): "Too few knocks here to rank" beside a handle at 375
          left the handle half a line. */}
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:justify-between sm:gap-3">
        <div className="min-w-0">
          {/* WRAPS, never truncates (wave 83): at 375 the handle read
              "@grantxfpw2…", and half a handle is not somebody to look up. */}
          <p className="break-all font-mono text-sm tracking-tight text-text-soft">
            {senderName(sender.handle, sender.display_name)}
          </p>
          {meta && <p className="mt-1 text-[12px] leading-relaxed text-faint">{meta}</p>}
        </div>
        <span
          data-pill={decision ? "decision" : "band"}
          className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 font-mono
                      text-[11px] tabular-nums ${pillClass}`}
        >
          {pill}
        </span>
      </div>

      {/**
       * THE OVERRIDE, SAID OUT LOUD (FR-6.4 Save).
       *
       * A saved knock that missed the bar is in the owner's Passed list because
       * the owner put it there, not because it earned the place — and an
       * override nobody can see is indistinguishable from a filter that does not
       * work. The words are the shared chip vocabulary's, imported rather than
       * retyped, so the card and the list row can never come to disagree.
       *
       * Only when the bar was actually missed: a Save on a knock that cleared it
       * anyway overrode nothing and has nothing to announce.
       */}
      {knock.saved && !s.cleared_bar && (
        <p className="mt-2">
          <span data-chip="saved" title={CHIP_FULL.saved}>
            {CHIP_FULL.saved}
          </span>
        </p>
      )}

      {/* What they said — full width, because it is what the card is for. */}
      <button
        type="button"
        onClick={handlers.onToggleExpand}
        aria-expanded={ui.expanded}
        className="mt-3 block w-full text-left focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70 focus-visible:ring-offset-2
                   focus-visible:ring-offset-bg rounded"
      >
        <span className="text-[15px] leading-relaxed text-text">
          {/* The guard's stop is said before anything about the hearing (§8):
              the recipient sees the reason on the card, and it is what happened
              to this hearing. It is not a verdict and the verbs stay. */}
          {/* And the owner's own decision is said before the hearing, for the
              same reason the pill says it (owner item 9): once a gate has
              moved, the hearing behind it is over — opening the door settles it
              outright — and a card that goes on promising "its read arrives
              when it is done" under a `Let through` pill is arguing with
              itself (b unnamed 2). The row says the same fact in its own
              length (`DECIDED_UNREAD_PREVIEW`). */}
          {knock.guard_closed
            ? guardClosedLine(knock.guard_reason)
            : assessed
              ? knock.summary
              : knock.gate === "at_door"
                ? HEARING_LINE
                : DECIDED_UNREAD_LINE}
        </span>
      </button>

      {knock.matched_categories.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {knock.matched_categories.map((category) => (
            <span
              key={category}
              className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase
                         tracking-[0.08em] text-faint"
            >
              {category}
            </span>
          ))}
        </div>
      )}

      {/* The work, never the money (R-0825-3): "spend as the costly signal"
          died with door-set prices — under one plan for both sides, spend says
          nothing about the sender, and a figure here invites ranking by it. */}
      {(vetting || (knock.has_attachments && !knock.attachments) || knock.replied) && (
        <p className="mt-2 font-mono text-[11px] tabular-nums text-faint">
          {[
            vetting,
            knock.has_attachments && !knock.attachments ? "files attached" : "",
            knock.replied ? "replied" : "",
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      {files.length > 0 && <FileList files={files} />}

      {unrecommended && knock.nob_call && (
        <p
          data-nob-call="not-recommended"
          className="mt-3 rounded-md border border-border-soft bg-surface px-3 py-2 text-[13px] leading-snug"
        >
          <span className="font-medium text-text-soft">{NOT_RECOMMENDED}</span>
          <span className="text-muted">
            {" · "}
            {nobCallReason(knock.nob_call, knock.guard_reason)}
          </span>
        </p>
      )}

      {/* The verbs, and the question about the ranking — which is not one of
          them. The four decisions are buttons; Yes/No stay quiet text, because
          they grade Nob's work rather than acting on the person (FR-6.4). */}
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border-soft pt-3">
        {/* THE VERBS ARE OFFERED AT EVERY MOMENT (§3), read published or not:
            the owner may open the door, decline or block on what they have read
            so far. They stop only where the gate has already opened — Save,
            Not for me, Block and Open the door are decisions made AT a door,
            and once it is open they are the wrong four things under a chat. */}
        {!through && (
          <>
            <VerbButton
              primary={!unrecommended}
              onClick={handlers.onReply}
              locked={locked("composerOpen")}
            >
              {OPEN_THE_DOOR}
            </VerbButton>
            {knock.has_booking && (
              <VerbButton onClick={handlers.onBook} locked={locked(null)}>
                Book
              </VerbButton>
            )}
            <VerbButton onClick={handlers.onSave} locked={locked(null)}>
              Save
            </VerbButton>
            <VerbButton onClick={handlers.onNotInterested} locked={locked("dismissOpen")}>
              {NOT_FOR_ME}
            </VerbButton>
            <VerbButton onClick={handlers.onBlock} locked={locked("blockOpen")}>
              {BLOCK_VERB}
            </VerbButton>
          </>
        )}

        {/* No ranking to grade until a read is published (§6.2). */}
        {assessed && (
          <span className="ml-auto flex items-center gap-3">
            {/* A question, not a caption (owner, 2026-08-26). "Nob's ranking"
              named the thing being graded but left the buttons orphaned; the
              question makes the two answers read as answers. */}
            <span className="text-[12px] uppercase tracking-[0.1em] text-fainter">
              Was Nob&rsquo;s ranking helpful?
            </span>
            <ActionButton
              onClick={handlers.onHelpful}
              disabled={locked("feedbackOpen")}
              aria-disabled={locked("feedbackOpen") || undefined}
              aria-pressed={ui.feedbackDone === "helpful"}
              className={ui.feedbackDone === "helpful" ? "text-accent-ink-strong" : ""}
            >
              Yes
            </ActionButton>
            <ActionButton
              onClick={handlers.onNotHelpful}
              disabled={locked("feedbackOpen")}
              aria-disabled={locked("feedbackOpen") || undefined}
              aria-pressed={ui.feedbackDone === "not_helpful"}
              className={ui.feedbackDone === "not_helpful" ? "text-accent-ink-strong" : ""}
            >
              No
            </ActionButton>
          </span>
        )}
      </div>

      {/* The dry confirm and the 30-second Undo are NOT here any more — see
          `ActionStatus` below, which the pane renders under its composer. */}

      {ui.feedbackDone && !ui.feedbackOpen && !ui.note && (
        <p className="mt-3 text-[13px] text-faint">Noted.</p>
      )}

      {/* Reply composer — a masked relay does the sending (FR-6.5). */}
      {ui.composerOpen && (
        <div className="mt-3 rounded-lg border border-border bg-surface/40 p-3">
          <label htmlFor={`reply-${knock.id}`} className="sr-only">
            Your reply to {senderName(sender.handle, sender.display_name)}
          </label>
          <textarea
            id={`reply-${knock.id}`}
            value={ui.replyDraft}
            onChange={(e) => handlers.onReplyDraft(e.target.value)}
            rows={3}
            /* Reply OPENS THE CONVERSATION: the owner pressed a verb meaning
               "talk to this person", and landing them in the box rather than
               beside it is the difference between a chat and a form. */
            autoFocus
            placeholder="Your reply. It sends from a relay — your address stays yours."
            className="w-full resize-y rounded-md border border-border bg-bg/60 p-2.5 text-sm
                       text-text placeholder:text-fainter focus-visible:border-border-loud
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40"
          />
          <div className="mt-2.5 flex items-center gap-3">
            <button
              type="button"
              onClick={handlers.onSendReply}
              disabled={ui.busy || ui.replyDraft.trim() === ""}
              className="inline-flex h-9 items-center rounded-lg bg-accent px-4 text-sm font-medium
                         text-accent-fg transition-colors duration-75 hover:bg-accent-soft
                         disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none
                         focus-visible:ring-2 focus-visible:ring-focus/70 focus-visible:ring-offset-2
                         focus-visible:ring-offset-bg"
            >
              Send
            </button>
            <ActionButton onClick={handlers.onCancelReply}>Cancel</ActionButton>
          </div>
        </div>
      )}

      {/* Not for me → a reason, before the dismissal (FR-6.4, owner item 10).
          Its own vocabulary, not the ranking's: this asks what the OWNER made
          of the person, and "Shouldn't have passed" only appears on a knock
          that actually got through the bar. */}
      {ui.dismissOpen && (
        <ChipPanel heading="Why is it not for you?" onCancel={handlers.onCancelDismiss}>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {dismissChipsFor(knock).map((chip) => (
              <Chip
                key={chip}
                onClick={() => handlers.onPickDismissReason(chip)}
                busy={ui.busy}
                pressed={chip === "other" && ui.dismissOther}
              >
                {DISMISS_LABEL[chip]}
              </Chip>
            ))}
          </div>
          {/* "Other" is a chip and a question in one: picking it dismisses
              nothing yet, it opens one line. Empty is a legitimate answer —
              the owner said "other" and had nothing to add — so Send is never
              disabled on emptiness, only while a write is in flight. */}
          {ui.dismissOther && (
            <div className="mt-2.5">
              <label htmlFor={`dismiss-note-${knock.id}`} className="sr-only">
                {DISMISS_OTHER_PLACEHOLDER}
              </label>
              <input
                id={`dismiss-note-${knock.id}`}
                type="text"
                value={ui.dismissNote}
                maxLength={DISMISS_NOTE_MAX}
                onChange={(e) => handlers.onDismissNote(e.target.value)}
                autoFocus
                placeholder={DISMISS_OTHER_PLACEHOLDER}
                className="w-full rounded-md border border-border bg-bg/60 px-2.5 py-2 text-sm
                           text-text placeholder:text-fainter focus-visible:border-border-loud
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40"
              />
              <div className="mt-2 flex items-center gap-3">
                <VerbButton onClick={handlers.onSendDismissNote} locked={ui.busy}>
                  {DISMISS_OTHER_SEND}
                </VerbButton>
                <span className="text-[12px] text-fainter">{DISMISS_OTHER_NOTE}</span>
              </div>
            </div>
          )}
        </ChipPanel>
      )}

      {/* Block → an inline confirm, since it is permanent at this door (FR-6.4).
          The second line is the part that needed saying once blocking started
          removing the conversation: the owner is agreeing to lose the row, and
          also to keep the record, and both halves are load-bearing. */}
      {ui.blockOpen && (
        <div className="mt-3 rounded-lg border border-border bg-surface/40 p-3">
          <p className="text-[14px] text-text-soft">{BLOCK_ASK}</p>
          <p className="mt-1 text-[13px] text-faint">{BLOCK_NOTE}</p>
          <div className="mt-2.5 flex items-center gap-3">
            <button
              type="button"
              onClick={handlers.onConfirmBlock}
              disabled={ui.busy}
              className="inline-flex h-9 items-center rounded-lg border border-border-strong px-4 text-sm
                         text-text transition-colors duration-75 hover:border-border-loud hover:bg-surface
                         disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none
                         focus-visible:ring-2 focus-visible:ring-focus/70 focus-visible:ring-offset-2
                         focus-visible:ring-offset-bg"
            >
              Block
            </button>
            <ActionButton onClick={handlers.onCancelBlock}>Keep at the door</ActionButton>
          </div>
        </div>
      )}

      {/* Not helpful → the reason chips for the ranking (FR-6.4, feeds FR-8).
          These are the ORIGINAL six, `shouldnt_surface` included — it is still
          the one chip the server acts on. */}
      {ui.feedbackOpen && (
        <ChipPanel heading="What was off about the ranking?" onCancel={handlers.onNotHelpful}>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {REASON_CHIPS.map((chip) => (
              <Chip key={chip} onClick={() => handlers.onPickFeedbackReason(chip)} busy={ui.busy}>
                {REASON_LABEL[chip]}
              </Chip>
            ))}
          </div>
        </ChipPanel>
      )}

      {/* The itemised detail behind the summary (FR-6.3). The grilling itself is
          no longer here — it is the folded block at the top of the card. */}
      {ui.expanded && (
        <div className="mt-4 border-t border-border-soft pt-4">
          <dl className="grid gap-x-8 gap-y-2 text-[13px] sm:grid-cols-2">
            {/* No standing row until a read is published (§6.2): "score 0"
                is a verdict the doorman has not reached. The work rows stay —
                a page read is a fact that exists. */}
            {/* THE DESK'S SCHEME (wave 83): Standing is the word — Strong,
                Reasonable, Weak — and Rank is where it sits among its
                neighbours. This row used to call the rank "Standing", one
                column away from a desk that meant the word by it. */}
            {assessed && <Detail term="Standing" value={BAND_WORD[s.band]} />}
            {assessed && <Detail term="Rank" value={rank ?? UNRANKED_LABEL} />}
            {assessed && <Detail term="Score" value={formatCount(s.raw_score)} />}
            <Detail term="Claims" value={claimsLine(receipts)} />
            <Detail
              term="Reviewed"
              value={`${counted(receipts.pages_reviewed, "page")} · ${counted(receipts.sources_checked, "source")}`}
            />
            {Object.entries(sender.contact).map(([channel, value]) => (
              // What the sender said when asked where they could be reached, in
              // their own words. The door-requested channels are gone (ruling
              // R-0828-2) and this is what is left: one line, only when the
              // sender wrote one instead of an address.
              <Detail
                key={channel}
                term={channel === "reply" ? "Reach them at" : channel}
                value={value}
              />
            ))}
            {knock.created_at && <Detail term="Received" value={relativeTime(knock.created_at)} />}
          </dl>
        </div>
      )}
    </li>
  );
}

/**
 * WHAT THE VERB DID, AND THE 30 SECONDS TO TAKE IT BACK (FR-6.4) — BESIDE THE
 * COMPOSER, NOT ON THE CARD (F-58, wave 17b).
 *
 * This was rendered inside the card, which was right while the card was the
 * whole screen and wrong the instant it stopped being one. Opening the door IS
 * the reply, and a reply moves the gate — so the pane swaps the knock card for
 * a through-thread in the same breath, and the card goes under the closed
 * `<details data-fold="hearing">` (`knock-chat.tsx`) carrying the confirmation
 * with it. Every reply that opened a door therefore hid its own "Sent.", its
 * relay-failure sentence and its Undo under a collapsed bar: `inDOM`, and
 * `visible=false`, for the whole of the window it was promised for.
 *
 * So it belongs to the COMPOSER, which is pinned at the foot of the pane on
 * every surface and never folds. The card carries nothing of it.
 */
export function ActionStatus({
  ui,
  onUndo,
}: {
  ui: Pick<CardUi, "note" | "pending">;
  onUndo: () => void;
}) {
  if (!ui.note && !ui.pending) return null;
  return (
    <div
      data-action-status
      className="mt-2 flex items-center gap-3 px-1 text-[13px]"
      aria-live="polite"
    >
      {ui.note && <span className="text-muted">{ui.note}</span>}
      {ui.pending && (
        <ActionButton onClick={onUndo} className="text-accent-ink/80 hover:text-accent-ink-strong">
          Undo
        </ActionButton>
      )}
    </div>
  );
}

/**
 * EACH FILE, BY NAME, AND WHETHER IT WAS READ (wave 83).
 *
 * It was one bare word, "attachment", beside "6 pages" — three PDFs of 12, 3
 * and 6 pages, one of them read, and the owner could see neither the other two
 * nor which one it was. "Read" is the server's fact: a document review that
 * completed on this file (`workItems.ts#hearingWorkFacts`).
 */
export const FILE_READ = "read";
export const FILE_NOT_READ = "not read";

function FileList({ files }: { files: RecipientAttachment[] }) {
  return (
    <ul data-files className="mt-2 flex flex-col gap-1 font-mono text-[11px] text-faint">
      {files.map((file) => (
        <li key={file.attachment_id} data-file-read={file.read} className="break-all">
          {file.filename}
          {file.page_count > 0 && !file.mime_type.startsWith("image/")
            ? ` · ${counted(file.page_count, "page")}`
            : ""}
          {" · "}
          <span className={file.read ? "text-accent-ink-strong" : ""}>
            {file.read ? FILE_READ : FILE_NOT_READ}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Detail({ term, value, note }: { term: string; value: string; note?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border-soft/70 pb-1.5">
      <dt className="text-faint">{term}</dt>
      <dd className="text-right font-mono tabular-nums text-text-soft" title={note}>
        {value}
      </dd>
    </div>
  );
}

/**
 * The box a question opens in: a heading, whatever answers it takes, and the
 * way back out. Two questions use it — "why is it not for you?" and "what was
 * off about the ranking?" — and they no longer share a chip list, so what goes
 * between the heading and "Never mind" is the caller's.
 */
function ChipPanel({
  heading,
  onCancel,
  children,
}: {
  heading: string;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3 rounded-lg border border-border bg-surface/40 p-3">
      <p className="text-[13px] text-muted">{heading}</p>
      {children}
      <div className="mt-2.5">
        <ActionButton onClick={onCancel} className="text-[13px]">
          Never mind
        </ActionButton>
      </div>
    </div>
  );
}

function Chip({
  onClick,
  busy,
  pressed,
  children,
}: {
  onClick: () => void;
  busy: boolean;
  /** Only the chips that TOGGLE something pass this — "Other" and its one line. */
  pressed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      {...(pressed === undefined ? {} : { "aria-pressed": pressed })}
      className={`rounded-full border px-3 py-1.5 text-[13px] transition-colors duration-75
                  hover:border-border-loud hover:text-text disabled:pointer-events-none
                  disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2
                  focus-visible:ring-focus/70 focus-visible:ring-offset-2
                  focus-visible:ring-offset-bg ${
                    pressed
                      ? "border-accent/60 bg-accent/10 text-accent-ink-strong"
                      : "border-border text-text-soft"
                  }`}
    >
      {children}
    </button>
  );
}
